// context/cartContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import { lsGet, lsSet, lsRemove } from '~/utils/safeStorage'
import {
  CartPredefinedValue,
  CartProductComponent,
} from '~/components/ProductPage/productTypeComponents/CartProductComponents'
import type { PricingQuoteResult } from '~/utils/pricingQuote'

/** ---- Types ---- */

// Sample payload stored in cart
export type SampleCartConfig = {
  id: string
  color?: string
  price?: number
  imageCarousel?: string[]
}

// Discriminated union for cart items
export type CartProductItem = {
  type: 'product'
  name: string
  config: CartProductComponent[]
  screenshot?: string
  assemblySelected?: boolean
  quantity: number
  /**
   * The exact /api/pricing/quote request + response that priced this item.
   * Present for quote-driven products (wardrobe, stand, bedside, tv-stand,
   * work-table, rack); absent for static-priced products (table). Forwarded
   * verbatim to /api/checkout — see Checkout.tsx.
   */
  quote?: PricingQuoteResult
}

export type CartSampleItem = {
  type: 'sample'
  name: string
  sample: SampleCartConfig
  quantity: number
}

export type CartItem = CartProductItem | CartSampleItem

// Overloaded Cart API
interface CartContextType {
  items: CartItem[]

  /** Add a PRODUCT (keeps predefined values logic) */
  addItem(
    name: string,
    config: CartProductComponent[],
    predefinedValues: CartPredefinedValue,
    screenshot?: string
  ): void

  /** Add a SAMPLE */
  addItem(name: string, sample: SampleCartConfig): void

  removeItem: (index: number) => void
  updateQuantity: (index: number, quantity: number) => void
  toggleAssembly: (index: number) => void
  clearCart: () => void
  itemCount: number
}

/** ---- Assembly helpers ---- */

const ASSEMBLY_RATE = 0.07

export function getAssemblyPrice(item: CartProductItem): number {
  const priceComp = item.config.find((c) => c.type === 'price')
  const price = priceComp?.predefinedValue ?? priceComp?.price ?? 0
  return Math.floor((price * ASSEMBLY_RATE) / 10) * 10
}

/** ---- Context ---- */

const CartContext = createContext<CartContextType | undefined>(undefined)
const LS_KEY = 'cartItems'

/** ---- Provider ---- */

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([])

  // Normalize legacy items ({ name, config }) to { type: 'product', ... }
  useEffect(() => {
    const stored = lsGet(LS_KEY)
    if (!stored) return
    try {
      const parsed = JSON.parse(stored) as Array<any>
      const normalized: CartItem[] = parsed.map((it) => {
        if (it && (it.type === 'product' || it.type === 'sample')) {
          return { ...it, quantity: it.quantity ?? 1 } as CartItem
        }
        // Legacy shape fallback
        return {
          type: 'product',
          name: it?.name ?? 'product',
          config: Array.isArray(it?.config) ? it.config : [],
          quantity: 1,
        } as CartProductItem
      })
      setItems(normalized)
    } catch {
      // Corrupt storage → reset
      lsRemove(LS_KEY)
      setItems([])
    }
  }, [])

  // Implementation handles both overloads
  const addItem = useCallback(
    (
      name: string,
      second: CartProductComponent[] | SampleCartConfig,
      predefinedValues?: CartPredefinedValue
    ) => {
      let newItem: CartItem

      if (Array.isArray(second)) {
        // PRODUCT: apply predefined values per component.type
        const newConfig = second.map((component) => {
          // predefinedValues is a map keyed by component.type
           
          // @ts-expect-error index by discriminated union
          const pv = predefinedValues?.[component.type]
          return pv ? { ...component, predefinedValue: pv } : component
        })
        // Extract screenshot + pricing quote from predefinedValues if provided
        const screenshot = (predefinedValues as any)?.screenshot as string | undefined
        const quote = (predefinedValues as any)?.quote as
          | PricingQuoteResult
          | undefined
        newItem = { type: 'product', name, config: newConfig, screenshot, quote, quantity: 1 }
      } else {
        // SAMPLE
        newItem = { type: 'sample', name, sample: second, quantity: 1 }
      }

      const next = [...items, newItem]
      setItems(next)
      lsSet(LS_KEY, JSON.stringify(next))
    },
    [items]
  ) as CartContextType['addItem'] // satisfy the overload type

  const removeItem = useCallback(
    (index: number) => {
      const next = items.filter((_, i) => i !== index)
      setItems(next)
      lsSet(LS_KEY, JSON.stringify(next))
    },
    [items]
  )

  const updateQuantity = useCallback(
    (index: number, quantity: number) => {
      const clamped = Math.max(1, quantity)
      const next = items.map((item, i) =>
        i === index ? { ...item, quantity: clamped } : item
      )
      setItems(next)
      lsSet(LS_KEY, JSON.stringify(next))
    },
    [items]
  )

  const toggleAssembly = useCallback(
    (index: number) => {
      const next = items.map((item, i) => {
        if (i !== index || item.type !== 'product') return item
        return { ...item, assemblySelected: !item.assemblySelected }
      })
      setItems(next)
      lsSet(LS_KEY, JSON.stringify(next))
    },
    [items]
  )

  const clearCart = useCallback(() => {
    setItems([])
    lsRemove(LS_KEY)
  }, [])

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQuantity, toggleAssembly, clearCart, itemCount: items.length }}
    >
      {children}
    </CartContext.Provider>
  )
}

/** ---- Hook ---- */

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within a CartProvider')
  return ctx
}
